import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, Bot, ListChecks, AlertTriangle, ArrowRight } from "lucide-react";
import { apiGet } from "@/lib/api";
import { useWorkspace, WORKSPACE_COLORS } from "@/context/WorkspaceContext";

export default function WorkspacesPage() {
  const [items, setItems] = useState([]);
  const { setWorkspace } = useWorkspace();

  useEffect(() => {
    const load = () => apiGet("/workspaces").then((records) => setItems(records.map((w) => ({ ...w, color: WORKSPACE_COLORS[w.id] || "#7896ab" })))).catch(() => {});
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <LayoutGrid className="w-6 h-6 text-jarvis-cyan" />
        <div>
          <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-cyan jv-glow">Workspaces</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest">{items.length} isolated environments</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((w) => (
          <motion.div
            key={w.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="jv-panel p-5 group cursor-pointer"
            style={{ borderColor: `${w.color}33` }}
            onClick={() => setWorkspace(w.id)}
            data-testid={`workspace-card-${w.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-mono text-base font-bold tracking-[0.15em]" style={{ color: w.color }}>{w.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{w.kind}</div>
              </div>
              <span className="w-2.5 h-2.5 rounded-full jv-pulse" style={{ backgroundColor: w.color, color: w.color }} />
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-md border border-white/8 bg-black/20 px-2 py-2 text-center">
                <Bot className="w-3.5 h-3.5 mx-auto text-slate-400 mb-1" />
                <div className="font-mono text-sm font-bold text-slate-100">{w.agents}</div>
                <div className="font-mono text-[8px] uppercase tracking-widest text-slate-600">Agents</div>
              </div>
              <div className="rounded-md border border-white/8 bg-black/20 px-2 py-2 text-center">
                <ListChecks className="w-3.5 h-3.5 mx-auto text-slate-400 mb-1" />
                <div className="font-mono text-sm font-bold text-slate-100">{w.running_tasks}</div>
                <div className="font-mono text-[8px] uppercase tracking-widest text-slate-600">Tasks</div>
              </div>
              <div className="rounded-md border border-white/8 bg-black/20 px-2 py-2 text-center">
                <AlertTriangle className="w-3.5 h-3.5 mx-auto text-jarvis-amber mb-1" />
                <div className="font-mono text-sm font-bold text-slate-100">{w.alerts}</div>
                <div className="font-mono text-[8px] uppercase tracking-widest text-slate-600">Alerts</div>
              </div>
            </div>

            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-jarvis-cyanBright transition-colors">
              <span data-testid={`workspace-infrastructure-${w.id}`}>Infrastructure: Not connected</span>
              <span className="flex items-center gap-1">Enter <ArrowRight className="w-3 h-3" /></span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
